import React, { useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Divider,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Modal from './Modal';
import { formatCurrency } from '../../Shared/stripeFee';
import { useStripePaymentsEnabled } from '../../Context/AppContext';

const MANUAL_METHODS = [
	{ value: 'check', label: 'Check' },
	{ value: 'venmo', label: 'Venmo' },
	{ value: 'other', label: 'Other' },
];

const APPLIED_TO_OPTIONS = [
	{ value: 'deposit', label: 'Deposit' },
	{ value: 'final', label: 'Balance / final payment' },
];

const emptyLine = (defaults = {}) => ({
	id: crypto.randomUUID(),
	applied_to: defaults.applied_to || 'deposit',
	method: defaults.method || 'check',
	amount: defaults.amount || '',
});

const getBreakdown = (job, bid) =>
	job?.refund_summary?.breakdown ?? bid?.capabilities?.refund?.breakdown ?? null;

const lineMethodOptions = ({
	appliedTo,
	breakdown,
	stripePaymentsEnabled,
}) => {
	const bucket = appliedTo === 'deposit' ? breakdown?.deposit : breakdown?.final;
	const options = [...MANUAL_METHODS];
	if (
		stripePaymentsEnabled &&
		Number(bucket?.stripe_refundable || 0) > 0
	) {
		options.unshift({ value: 'stripe', label: 'Card (Stripe)' });
	}
	return options;
};

const BreakdownRow = ({ label, bucket }) => {
	if (!bucket || Number(bucket.collected || 0) <= 0) {
		return null;
	}
	return (
		<Stack
			direction="row"
			justifyContent="space-between"
			spacing={2}
			sx={{ py: 0.75 }}
		>
			<Typography variant="body2" fontWeight={600}>
				{label}
			</Typography>
			<Stack spacing={0.25} alignItems="flex-end">
				<Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
					${formatCurrency(bucket.collected)} collected
				</Typography>
				<Typography
					variant="caption"
					color="text.secondary"
					sx={{ fontVariantNumeric: 'tabular-nums' }}
				>
					${formatCurrency(bucket.refundable)} available
					{Number(bucket.stripe_refundable || 0) > 0
						? ` · $${formatCurrency(bucket.stripe_refundable)} to card`
						: ''}
				</Typography>
			</Stack>
		</Stack>
	);
};

const RecordRefundModal = ({
	open,
	onClose,
	job,
	bid,
	onSubmit,
	submitting = false,
}) => {
	const stripePaymentsEnabled = useStripePaymentsEnabled();
	const breakdown = getBreakdown(job, bid);
	const totalRefundable = Number(
		breakdown?.total_refundable ??
			job?.refund_summary?.refundable_balance ??
			bid?.capabilities?.refund?.refundable_balance ??
			0,
	);

	const availableTargets = useMemo(() => {
		return APPLIED_TO_OPTIONS.filter((option) => {
			const bucket =
				option.value === 'deposit' ? breakdown?.deposit : breakdown?.final;
			return Number(bucket?.refundable || 0) > 0;
		});
	}, [breakdown]);

	const defaultTarget = availableTargets[0]?.value || 'deposit';
	const defaultMethod =
		lineMethodOptions({
			appliedTo: defaultTarget,
			breakdown,
			stripePaymentsEnabled,
		})[0]?.value || 'check';

	const [lines, setLines] = useState([emptyLine()]);
	const [reason, setReason] = useState('');
	const [notes, setNotes] = useState('');

	useEffect(() => {
		if (!open) {
			return;
		}
		setReason('');
		setNotes('');
		setLines([
			emptyLine({
				applied_to: defaultTarget,
				method: defaultMethod,
			}),
		]);
	}, [open, defaultTarget, defaultMethod]);

	const bucketRemaining = useMemo(() => {
		const depositUsed = lines
			.filter((line) => line.applied_to === 'deposit')
			.reduce((sum, line) => sum + (Number(line.amount) || 0), 0);
		const finalUsed = lines
			.filter((line) => line.applied_to === 'final')
			.reduce((sum, line) => sum + (Number(line.amount) || 0), 0);
		const depositStripeUsed = lines
			.filter(
				(line) => line.applied_to === 'deposit' && line.method === 'stripe',
			)
			.reduce((sum, line) => sum + (Number(line.amount) || 0), 0);
		const finalStripeUsed = lines
			.filter(
				(line) => line.applied_to === 'final' && line.method === 'stripe',
			)
			.reduce((sum, line) => sum + (Number(line.amount) || 0), 0);

		return {
			deposit: Math.max(
				0,
				Number(breakdown?.deposit?.refundable || 0) - depositUsed,
			),
			final: Math.max(
				0,
				Number(breakdown?.final?.refundable || 0) - finalUsed,
			),
			depositStripe: Math.max(
				0,
				Number(breakdown?.deposit?.stripe_refundable || 0) - depositStripeUsed,
			),
			finalStripe: Math.max(
				0,
				Number(breakdown?.final?.stripe_refundable || 0) - finalStripeUsed,
			),
		};
	}, [lines, breakdown]);

	const totalRefundAmount = lines.reduce(
		(sum, line) => sum + (Number(line.amount) || 0),
		0,
	);

	const lineErrors = lines.map((line) => {
		const amount = Number(line.amount);
		if (!line.amount || Number.isNaN(amount) || amount <= 0) {
			return null;
		}
		const bucketKey = line.applied_to === 'deposit' ? 'deposit' : 'final';
		const bucketLimit =
			bucketKey === 'deposit'
				? Number(breakdown?.deposit?.refundable || 0)
				: Number(breakdown?.final?.refundable || 0);
		const bucketUsed = lines
			.filter((entry) => entry.applied_to === line.applied_to)
			.reduce((sum, entry) => sum + (Number(entry.amount) || 0), 0);
		if (bucketUsed > bucketLimit + 0.001) {
			return `Exceeds available ${line.applied_to === 'deposit' ? 'deposit' : 'balance'}`;
		}
		if (line.method === 'stripe') {
			const stripeLimit =
				bucketKey === 'deposit'
					? Number(breakdown?.deposit?.stripe_refundable || 0)
					: Number(breakdown?.final?.stripe_refundable || 0);
			const stripeUsed = lines
				.filter(
					(entry) =>
						entry.applied_to === line.applied_to && entry.method === 'stripe',
				)
				.reduce((sum, entry) => sum + (Number(entry.amount) || 0), 0);
			if (stripeUsed > stripeLimit + 0.001) {
				return 'Exceeds card refund limit';
			}
		}
		return null;
	});

	const hasValidLines = lines.some((line) => {
		const amount = Number(line.amount);
		return line.amount && !Number.isNaN(amount) && amount > 0;
	});
	const hasLineErrors = lineErrors.some(Boolean);
	const exceedsTotal = totalRefundAmount > totalRefundable + 0.001;

	const updateLine = (id, patch) => {
		setLines((prev) =>
			prev.map((line) => {
				if (line.id !== id) {
					return line;
				}
				const next = { ...line, ...patch };
				if (patch.applied_to && patch.applied_to !== line.applied_to) {
					const methods = lineMethodOptions({
						appliedTo: patch.applied_to,
						breakdown,
						stripePaymentsEnabled,
					});
					if (!methods.some((option) => option.value === next.method)) {
						next.method = methods[0]?.value || 'check';
					}
				}
				return next;
			}),
		);
	};

	const addLine = () => {
		const target =
			bucketRemaining.deposit > 0
				? 'deposit'
				: bucketRemaining.final > 0
					? 'final'
					: defaultTarget;
		const method =
			lineMethodOptions({
				appliedTo: target,
				breakdown,
				stripePaymentsEnabled,
			})[0]?.value || 'check';
		setLines((prev) => [...prev, emptyLine({ applied_to: target, method })]);
	};

	const handleSubmit = () => {
		const items = lines
			.map((line) => ({
				applied_to: line.applied_to,
				method: line.method,
				amount: Number(line.amount),
			}))
			.filter((item) => item.amount > 0);
		onSubmit({
			items,
			reason,
			notes,
			bid: bid?.id,
		});
	};

	return (
		<Modal
			open={open}
			onClose={onClose}
			modalTitle="Record refund"
			maxWidth="sm"
			alwaysMaxWidth
			useActionButtons
			actionButtons={
				<Box sx={{ display: 'flex', gap: 1 }}>
					<Button onClick={onClose} disabled={submitting}>
						Cancel
					</Button>
					<Button
						variant="contained"
						color="warning"
						onClick={handleSubmit}
						disabled={
							submitting ||
							!hasValidLines ||
							hasLineErrors ||
							exceedsTotal
						}
					>
						{submitting ? 'Processing...' : 'Confirm refund'}
					</Button>
				</Box>
			}
		>
			<Stack spacing={2.5} sx={{ py: 1 }}>
				<Box
					sx={{
						px: 2,
						py: 1.5,
						borderRadius: 2,
						border: '1px solid',
						borderColor: 'divider',
						bgcolor: 'action.hover',
					}}
				>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
						Available to refund
					</Typography>
					<Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
						${formatCurrency(totalRefundable)}
					</Typography>
					<BreakdownRow label="Deposit" bucket={breakdown?.deposit} />
					<BreakdownRow label="Balance" bucket={breakdown?.final} />
				</Box>

				<Stack spacing={2}>
					<Typography variant="subtitle2" fontWeight={700}>
						Refund lines
					</Typography>
					{lines.map((line, index) => {
						const methods = lineMethodOptions({
							appliedTo: line.applied_to,
							breakdown,
							stripePaymentsEnabled,
						});
						const error = lineErrors[index];
						return (
							<Box
								key={line.id}
								sx={{
									p: 2,
									borderRadius: 2,
									border: '1px solid',
									borderColor: error ? 'warning.main' : 'divider',
								}}
							>
								<Stack spacing={1.5}>
									<Stack
										direction="row"
										justifyContent="space-between"
										alignItems="center"
									>
										<Typography variant="body2" fontWeight={600}>
											Line {index + 1}
										</Typography>
										{lines.length > 1 ? (
											<IconButton
												size="small"
												onClick={() =>
													setLines((prev) =>
														prev.filter((entry) => entry.id !== line.id),
													)
												}
												aria-label="Remove refund line"
											>
												<DeleteOutlineIcon fontSize="small" />
											</IconButton>
										) : null}
									</Stack>

									{availableTargets.length > 1 ? (
										<FormControl fullWidth size="small">
											<InputLabel id={`refund-target-${line.id}`}>
												Apply to
											</InputLabel>
											<Select
												labelId={`refund-target-${line.id}`}
												label="Apply to"
												value={line.applied_to}
												onChange={(e) =>
													updateLine(line.id, { applied_to: e.target.value })
												}
											>
												{availableTargets.map((option) => (
													<MenuItem key={option.value} value={option.value}>
														{option.label}
													</MenuItem>
												))}
											</Select>
										</FormControl>
									) : (
										<Typography variant="body2" color="text.secondary">
											Applies to:{' '}
											<Box component="span" sx={{ fontWeight: 600 }}>
												{availableTargets[0]?.label || 'Deposit'}
											</Box>
										</Typography>
									)}

									<FormControl fullWidth size="small">
										<InputLabel id={`refund-method-${line.id}`}>
											How you&apos;ll refund
										</InputLabel>
										<Select
											labelId={`refund-method-${line.id}`}
											label="How you'll refund"
											value={line.method}
											onChange={(e) =>
												updateLine(line.id, { method: e.target.value })
											}
										>
											{methods.map((option) => (
												<MenuItem key={option.value} value={option.value}>
													{option.label}
												</MenuItem>
											))}
										</Select>
									</FormControl>

									<TextField
										label="Amount"
										type="number"
										size="small"
										fullWidth
										value={line.amount}
										onChange={(e) =>
											updateLine(line.id, { amount: e.target.value })
										}
										inputProps={{ min: 0.01, step: '0.01' }}
										error={Boolean(error)}
										helperText={
											error ||
											`Remaining for this ${line.applied_to === 'deposit' ? 'deposit' : 'balance'}: $${formatCurrency(
												line.applied_to === 'deposit'
													? bucketRemaining.deposit
													: bucketRemaining.final,
											)}`
										}
									/>
								</Stack>
							</Box>
						);
					})}

					<Button
						startIcon={<AddIcon />}
						onClick={addLine}
						sx={{ alignSelf: 'flex-start' }}
					>
						Add another refund
					</Button>
				</Stack>

				<Divider />

				<Stack direction="row" justifyContent="space-between" alignItems="center">
					<Typography variant="body2" color="text.secondary">
						Total refunding
					</Typography>
					<Typography variant="h6" sx={{ fontWeight: 700 }}>
						${formatCurrency(totalRefundAmount)}
					</Typography>
				</Stack>

				{exceedsTotal ? (
					<Alert severity="warning">
						Total exceeds the available refundable balance.
					</Alert>
				) : null}

				<TextField
					label="Reason"
					fullWidth
					value={reason}
					onChange={(e) => setReason(e.target.value)}
					placeholder="e.g. Customer canceled after deposit"
				/>

				<TextField
					label="Notes (optional)"
					fullWidth
					multiline
					minRows={2}
					value={notes}
					onChange={(e) => setNotes(e.target.value)}
				/>
			</Stack>
		</Modal>
	);
};

export default RecordRefundModal;
